import { ChevronLeft, EllipsisVertical, Funnel, RefreshCw } from "lucide-react";
import { ReactNode, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { IgButton, IgPaper, IgTypography } from "@intelligems/ig-theme";
import { Badge, Box, Skeleton, Stack, useTheme } from "@mui/material";
import { useAndSetExperienceInfoStateFromState } from "@src/api/experiences/use-and-set-experience-info-state-from-state";
import { useAndSetGenericExperimentFromUrl } from "@src/api/experiences/use-and-set-generic-experiment-from-url";
import { useClearExperienceOnUnmount } from "@src/features/experiments/hooks/use-clear-experience-on-unmount";
import { useSetViewActionAnyExperience } from "@src/features/experiments/hooks/use-set-view-action-any-experience";
import { StatusRenderer } from "@src/features/experiments/pages/home/components/experiencesList/StatusRenderer";
import { FilterDrawerContent } from "@src/features/mobileAnalytics/drawers/FilterDrawerContent";
import { MobileDrawerManager } from "@src/features/mobileAnalytics/drawers/MobileDrawerManager";
import { MoreActionsDrawerContent } from "@src/features/mobileAnalytics/drawers/MoreActionsDrawerContent";
import { useMobileDrawer } from "@src/features/mobileAnalytics/shared/hooks/useMobileDrawer";
import MobileFooter from "@src/features/mobileAnalytics/shared/layout/MobileFooter";
import { MobileDrawerProvider } from "@src/features/mobileAnalytics/shared/providers/MobileDrawerProvider";
import { DrawerTypes } from "@src/features/mobileAnalytics/shared/providers/types";
import { MobileAnalyticsDashboard } from "@src/features/mobileAnalytics/testDetails/MobileAnalyticsDashboard";
import { ShareLinkBanner } from "@src/features/publicShareLinks/components/ShareLinkBanner";
import { useReadOnlyMode } from "@src/features/publicShareLinks/hooks/useReadOnlyMode";
import { queryClient } from "@src/queryClient";
import { EXPERIMENT_ROUTES } from "@src/routes/routes";
import { useDispatch, useSelector } from "@src/store";
import {
  clearDashboardFilters,
  getExperienceAnalyticsFilters,
  refreshData,
} from "@src/store/slices/dashboardFilters";
import { selectedExperience } from "@src/store/slices/experiences";
import { Experience } from "@src/store/slices/experiences/types";
import { useIsFetching } from "@tanstack/react-query";

import { getActiveFilterCount } from "../shared/utils/getActiveFilterCount";

export const MobileTestDetails = () => {
  return (
    <MobileDrawerProvider>
      <MobileTestDetailsContent />
    </MobileDrawerProvider>
  );
};

const MobileTestDetailsContent = () => {
  const { isReadOnly } = useReadOnlyMode();

  // Always call hooks (React Rules of Hooks)
  // In read-only mode, these hooks will receive already-hydrated Redux state
  const { experienceState, query } = useAndSetGenericExperimentFromUrl();
  useAndSetExperienceInfoStateFromState(query);
  useSetViewActionAnyExperience(experienceState);
  useClearExperienceOnUnmount("experiment");

  const experience = useSelector(selectedExperience);
  const dashboardFilters = useSelector(getExperienceAnalyticsFilters);
  const theme = useTheme();
  const drawer = useMobileDrawer();

  // Calculate active filter count
  const filterCount = getActiveFilterCount(dashboardFilters);

  useEffect(() => {
    drawer.registerDrawer({
      type: DrawerTypes.FILTER,
      header: "Filters",
      headerLeftAdornment: isReadOnly ? null : <ClearFiltersButton />,
      content: <FilterDrawerContent />,
      maxHeight: "75svh",
    });

    // More actions drawer only in non-read-only mode
    if (experience && !isReadOnly) {
      drawer.registerDrawer({
        type: DrawerTypes.MORE_ACTIONS,
        header: "Options",
        content: (
          <MoreActionsDrawerContent
            experience={experience}
            onClose={drawer.closeDrawer}
          />
        ),
      });
    }
  }, [experience, drawer.registerDrawer, drawer.closeDrawer, isReadOnly]);

  let body: ReactNode;

  if (!experienceState.isSuccess || !experience) {
    body = <SkeletonLoader />;
  } else {
    body = <MobileAnalyticsDashboard experience={experience} />;
  }

  return (
    <Stack
      id="ig-mobile-test-details-root"
      direction="column"
      sx={{
        backgroundColor: theme.palette.grey[600],
      }}
    >
      <MobileTestDetailsHeader
        experiment={experience}
        filterCount={filterCount}
        onClickFilter={() => drawer.openDrawer(DrawerTypes.FILTER)}
        onClickMoreOptions={
          isReadOnly
            ? undefined
            : () => drawer.openDrawer(DrawerTypes.MORE_ACTIONS)
        }
        isReadOnly={isReadOnly}
      />

      {isReadOnly && <ShareLinkBanner />}

      {body}

      <MobileDrawerManager />

      {!isReadOnly && <MobileFooter />}
    </Stack>
  );
};

const ClearFiltersButton = () => {
  const dispatch = useDispatch();

  const handleClearFilters = () => {
    dispatch(clearDashboardFilters());
  };

  return (
    <IgButton
      variant="text"
      sx={{
        color: (theme) => theme.igPrimitives.base[500],
        fontWeight: 500,
        fontSize: 17,
      }}
      onClick={handleClearFilters}
    >
      Clear Filters
    </IgButton>
  );
};

type MobileTestDetailsHeaderProps = {
  experiment?: Experience;
  filterCount: number;
  onClickFilter: () => void;
  onClickMoreOptions?: () => void;
  isReadOnly?: boolean;
};

const MobileTestDetailsHeader = ({
  experiment,
  filterCount,
  onClickFilter,
  onClickMoreOptions,
  isReadOnly = false,
}: MobileTestDetailsHeaderProps) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch();

  const predicate = (query) =>
    query.queryKey.some(
      (key) =>
        typeof key === "object" && JSON.stringify(key).includes("dashboards")
    );
  const isFetching = useIsFetching({ predicate });

  function handleRefresh() {
    dispatch(refreshData());
    void queryClient.invalidateQueries({ predicate });
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        position: "sticky",
        zIndex: 1300,
        width: "100%",
        top: 0,
        px: 3,
        pt: 2,
        pb: 3,
        borderBottom: `1px solid ${theme.palette.grey[800]}`,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      {/* Back button - hide in read-only (no navigation in share) */}
      {!isReadOnly && (
        <IgButton
          size="small"
          variant="text"
          sx={{ minWidth: "auto", paddingX: 2.5 }}
          onClick={() => void navigate(EXPERIMENT_ROUTES.Experiments)}
        >
          <ChevronLeft />
        </IgButton>
      )}
      <Stack sx={{ flex: 1, minWidth: 0, position: "relative", top: 12 }}>
        <IgTypography
          fontSize={14}
          fontWeight={600}
          sx={{
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          {experiment?.name ? experiment.name : <Skeleton />}
        </IgTypography>

        <Stack direction="row">
          {experiment && (
            <Box sx={{ position: "relative", left: -6.5, top: -4 }}>
              <StatusRenderer
                variant="body2"
                experience={experiment}
                displayVariant={"textAndDot"}
              />
            </Box>
          )}
        </Stack>
      </Stack>
      {/* Filter button - always visible */}
      <IgButton
        size="small"
        variant="text"
        sx={{ minWidth: "auto", paddingX: 2.5 }}
        onClick={onClickFilter}
      >
        <FunnelWithBadge count={filterCount} />
      </IgButton>
      {/* Refresh button - hide in read-only */}
      {!isReadOnly && (
        <IgButton
          size="small"
          variant="text"
          sx={{ minWidth: "auto", paddingX: 2.5 }}
          onClick={handleRefresh}
        >
          <RefreshCw
            style={{
              animation: isFetching ? `spin 1s linear infinite` : undefined,
            }}
          />
        </IgButton>
      )}
      {/* More actions button - hide in read-only */}
      {!isReadOnly && onClickMoreOptions && (
        <IgButton
          size="small"
          variant="text"
          sx={{ minWidth: "auto", paddingX: 2.5 }}
          onClick={onClickMoreOptions}
        >
          <EllipsisVertical />
        </IgButton>
      )}
    </Stack>
  );
};

const FunnelWithBadge = ({ count }: { count: number }) => {
  return (
    <Badge
      badgeContent={count}
      color="error"
      overlap="circular"
      sx={{
        "& .MuiBadge-badge": {
          fontSize: "0.5rem",
          minWidth: "16px",
          height: "16px",
          padding: "0 4px",
          backgroundColor: (theme) => theme.igPrimitives.base[500],
        },
      }}
    >
      <Funnel />
    </Badge>
  );
};

const SkeletonLoader = () => {
  return (
    <Stack spacing={4} sx={{ padding: 4, paddingTop: 6 }}>
      {/* Header */}
      <Skeleton variant="rectangular" sx={{ mb: 2, borderRadius: 2 }} />

      {/* Stats Cards */}
      <Stack direction="row" spacing={2}>
        {[1, 2, 3].map((item) => (
          <IgPaper key={item} sx={{ flex: 1, p: 2, borderRadius: 2 }}>
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="80%" height={32} />
          </IgPaper>
        ))}
      </Stack>

      {/* AI Summary */}
      <Skeleton variant="rectangular" sx={{ borderRadius: 2 }} />

      {/* Metric Cards */}
      {[1, 2].map((card) => (
        <IgPaper key={card} sx={{ p: 3, borderRadius: 2 }}>
          <Skeleton variant="text" width={150} height={24} />
          <Skeleton variant="text" width={200} height={32} />
          <Skeleton
            variant="rectangular"
            height={180}
            sx={{ borderRadius: 1 }}
          />
        </IgPaper>
      ))}
    </Stack>
  );
};
